import { useCallback } from "react";
import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import {
  fetchTrackingRoutes,
  createTrackingRoute,
  updateTrackingRoute,
  deleteTrackingRoute,
  type TrackingRoute,
  type RouteUpsertPayload,
} from "../services/tracking/routesService";

const KEY = "tracking-routes";

export function useTrackingRoutes() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, error, isLoading, mutate } = useSWR<TrackingRoute[]>(
    token ? [KEY, token] : null,
    () => fetchTrackingRoutes(token),
    { revalidateOnFocus: false }
  );

  const refetch = useCallback(() => mutate(), [mutate]);
  const list = Array.isArray(data) ? data : [];

  const create = useCallback(
    async (payload: RouteUpsertPayload) => {
      const created = await createTrackingRoute(token, payload);
      await mutate();
      return created;
    },
    [token, mutate]
  );

  const update = useCallback(
    async (id: string, payload: RouteUpsertPayload) => {
      const updated = await updateTrackingRoute(token, id, payload);
      await mutate();
      return updated;
    },
    [token, mutate]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteTrackingRoute(token, id);
      await mutate();
    },
    [token, mutate]
  );

  return {
    routes: list,
    isLoading,
    error,
    refetch,
    create,
    update,
    remove,
    accessToken: token,
  };
}

export type { TrackingRoute, RouteUpsertPayload };
